/**
 * MasterListsRich — Master List editor with rich semantic schemas.
 * Enhanced with Department propagation & reassignment modal (Parts A, B, C).
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Layers, Save, Plus, Trash2, Loader2, AlertCircle, Sparkles,
  X, Users, ChevronRight, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '../../lib/api';

function FieldEditor({ field, value, onChange }) {
  if (field.type === 'bool') {
    return (
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5" data-testid={`f-${field.id}`} />
    );
  }
  if (field.type === 'number') {
    return (
      <input type="number" value={value ?? ''} step="0.1"
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
        data-testid={`f-${field.id}`} />
    );
  }
  if (field.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
        data-testid={`f-${field.id}`}>
        {(field.options || []).map(o => <option key={o} value={o}>{o || '— none —'}</option>)}
      </select>
    );
  }
  return (
    <input type={field.type === 'email' ? 'email' : 'text'} value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
      data-testid={`f-${field.id}`} />
  );
}

export default function MasterListsRich() {
  const [schemas, setSchemas] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [schema, setSchema] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entryCounts, setEntryCounts] = useState({});
  const [reassignModal, setReassignModal] = useState(null); // {removedDepts, affectedUsers, availableDepts, pendingEntries}
  const [renaming, setRenaming] = useState(null); // old department label being renamed

  // Track last-saved entries so we can detect removals on next save
  const lastSavedEntriesRef = useRef([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiGet('/masters/schemas');
        setSchemas(r.schemas || {});
        if (!selectedKey) {
          setSelectedKey(Object.keys(r.schemas)[0]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load master schemas');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const keys = Object.keys(schemas);
    if (!keys.length) return;
    let cancelled = false;
    (async () => {
      const counts = {};
      await Promise.all(keys.map(async (k) => {
        try {
          const r = await apiGet(`/masters/${k}/rich`);
          counts[k] = (r.entries || []).length;
        } catch { counts[k] = 0; }
      }));
      if (!cancelled) setEntryCounts(counts);
    })();
    return () => { cancelled = true; };
  }, [schemas]);

  const loadEntries = useCallback(async () => {
    if (!selectedKey) return;
    try {
      const r = await apiGet(`/masters/${selectedKey}/rich`);
      setSchema(r.schema);
      // Item 3 — mark rows that already existed on load as "_existing" so
      // departments get the explicit Rename action instead of a free-text
      // label field (which is what fed the old, fragile diff heuristic).
      const loaded = (r.entries || []).map(e => ({ ...e, _existing: true }));
      setEntries(loaded);
      lastSavedEntriesRef.current = loaded;
      setEntryCounts((c) => ({ ...c, [selectedKey]: loaded.length }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load entries');
    }
  }, [selectedKey]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const updateEntry = (idx, fieldId, val) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, [fieldId]: val } : e));
  };

  const addEntry = () => {
    const blank = {};
    for (const f of schema.fields) {
      blank[f.id] = f.default !== undefined ? f.default : (f.type === 'bool' ? false : (f.type === 'number' ? 0 : ''));
    }
    setEntries(prev => [...prev, blank]);
  };

  const removeEntry = (idx) => setEntries(prev => prev.filter((_, i) => i !== idx));

  const doSave = async (entriesToSave) => {
    setSaving(true);
    try {
      const clean = entriesToSave.map(({ _existing, ...rest }) => rest);
      const r = await apiPut(`/masters/${selectedKey}/rich`, { entries: clean });
      toast.success(`Saved ${r.count} entries for ${schema.label}`);
      const loaded = (r.entries || []).map(e => ({ ...e, _existing: true }));
      setEntries(loaded);
      lastSavedEntriesRef.current = loaded;
      setEntryCounts((c) => ({ ...c, [selectedKey]: loaded.length }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'department_has_users') {
        toast.error('Cannot save: departments with assigned users must be reassigned first.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    // For departments: check if any rows being removed have assigned users
    if (selectedKey === 'departments') {
      const labelField = 'label';
      const savedLabels = new Set(
        lastSavedEntriesRef.current.map(e => (e[labelField] || '').trim()).filter(Boolean)
      );
      const currentLabels = new Set(
        entries.map(e => (e[labelField] || '').trim()).filter(Boolean)
      );
      const removedDepts = [...savedLabels].filter(d => !currentLabels.has(d));

      if (removedDepts.length > 0) {
        // Check if any removed depts have users
        setSaving(true);
        try {
          const r = await apiGet(`/masters/departments/check-users?departments=${encodeURIComponent(removedDepts.join(','))}`);
          setSaving(false);
          const blocked = r.blocked || [];
          if (blocked.length > 0) {
            const affectedUsers = blocked.flatMap(b => b.users || []);
            const availableDepts = entries.map(e => (e[labelField] || '').trim()).filter(Boolean);
            setReassignModal({
              removedDepts: removedDepts,
              affectedUsers,
              availableDepts,
              pendingEntries: entries,
            });
            return; // don't save yet
          }
        } catch {
          setSaving(false);
        }
      }
    }
    await doSave(entries);
  };

  return (
    <div className="bg-[#F8F5EE] min-h-screen" data-testid="masters-rich">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <Link to="/app/settings" className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Back to Settings
          </Link>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Layers size={20} className="text-[#3b4872]" /> Master Lists · with Semantics
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Define the BEHAVIOUR of each master entry — not just its label. E.g. a custom attendance code can declare whether it counts as paid, present, or consumes leave balance. Super Admin only.
          </p>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            {/* Sidebar */}
            <aside className="col-span-3 bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">
                Masters · {Object.keys(schemas).length}
              </div>
              <ul>
                {Object.entries(schemas).map(([k, s]) => (
                  <li key={k}>
                    <button onClick={() => setSelectedKey(k)}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 hover:bg-slate-50 ${selectedKey === k ? 'bg-indigo-50 border-l-4 border-l-[#3b4872]' : ''}`}
                      data-testid={`master-pick-${k}`}
                    >
                      <div className="font-display font-bold">{s.label}</div>
                      <div className="text-[10px] text-slate-500">{entryCounts[k] != null ? `${entryCounts[k]} entries` : '…'} · {s.fields.length} cols</div>
                      {(s.wired_into || []).filter(w => !w.startsWith('(future)')).length > 0 && (
                        <div className="text-[9px] text-emerald-600 mt-0.5 inline-flex items-center gap-1">
                          <Sparkles size={8} /> Wired
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Main */}
            <main className="col-span-9 bg-white border border-slate-200 rounded-sm">
              {!schema ? (
                <div className="p-8 text-center text-sm text-slate-500">Select a master to configure.</div>
              ) : (
                <>
                  <div className="px-5 py-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="font-display font-bold text-lg">{schema.label}</h2>
                        <div className="text-[11px] text-slate-500">{schema.description}</div>
                        {selectedKey === 'departments' && (
                          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-2 inline-flex items-center gap-1">
                            <AlertCircle size={10} />
                            Renaming a department automatically updates all assigned employees, users, and RBAC profiles.
                            Deleting is blocked until all assigned users are reassigned.
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={addEntry}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-indigo-50"
                          data-testid="add-entry">
                          <Plus size={11} /> Add Entry
                        </button>
                        <button onClick={save} disabled={saving}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] disabled:opacity-50"
                          data-testid="save-master">
                          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                    {schema.wired_into && (
                      <div className="mt-2 text-[10px] text-slate-500 inline-flex items-center gap-1">
                        <Sparkles size={10} className="text-emerald-500" />
                        <strong>Wired into:</strong> {schema.wired_into.join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                          {schema.fields.map(f => (
                            <th key={f.id} className="text-left px-3 py-2 font-display font-bold">
                              {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
                              {f.help && <div className="text-[9px] text-slate-400 font-normal normal-case">{f.help}</div>}
                            </th>
                          ))}
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {entries.length === 0 ? (
                          <tr><td colSpan={schema.fields.length + 1} className="px-4 py-6 text-center text-slate-400 italic">No entries. Click "Add Entry" to start.</td></tr>
                        ) : entries.map((entry, idx) => (
                          <tr key={idx} className="hover:bg-slate-50" data-testid={`entry-row-${idx}`}>
                            {schema.fields.map(f => (
                              <td key={f.id} className="px-3 py-1.5 align-top">
                                {selectedKey === 'departments' && f.id === 'label' && entry._existing ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-slate-800">{entry.label}</span>
                                    <button onClick={() => setRenaming(entry.label)}
                                      className="text-slate-400 hover:text-[#3b4872]"
                                      title="Rename (propagates to profiles, users & employees)"
                                      data-testid={`rename-btn-${idx}`}>
                                      <Pencil size={11} />
                                    </button>
                                  </div>
                                ) : (
                                  <FieldEditor field={f} value={entry[f.id]}
                                    onChange={(v) => updateEntry(idx, f.id, v)} />
                                )}
                              </td>
                            ))}
                            <td className="px-3 py-1.5 text-right">
                              <button onClick={() => removeEntry(idx)} className="text-slate-400 hover:text-rose-600"
                                data-testid={`remove-${idx}`}>
                                <Trash2 size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t border-slate-200 text-[10px] text-slate-500 flex items-start gap-2">
                    <AlertCircle size={12} className="text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      Entries marked "Wired" actively drive business logic. For "future" labels, the semantics are stored but business code hasn't consumed them yet — adding one wire-up is a 1-line backend change.
                    </div>
                  </div>
                </>
              )}
            </main>
          </div>
        )}
      </div>

      {/* B2: Department Reassignment Modal */}
      {reassignModal && (
        <DeptReassignModal
          removedDepts={reassignModal.removedDepts}
          affectedUsers={reassignModal.affectedUsers}
          availableDepts={reassignModal.availableDepts}
          pendingEntries={reassignModal.pendingEntries}
          onComplete={async (finalEntries) => {
            setReassignModal(null);
            await doSave(finalEntries);
          }}
          onCancel={() => {
            // Restore the removed entries to local state
            setEntries(lastSavedEntriesRef.current);
            setReassignModal(null);
          }}
        />
      )}

      {/* Item 3 — explicit department rename, cascades unconditionally every time */}
      {renaming && (
        <RenameDeptModal
          oldName={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={() => { setRenaming(null); loadEntries(); }}
        />
      )}
    </div>
  );
}


// ─── RenameDeptModal ──────────────────────────────────────────────────────────
function RenameDeptModal({ oldName, onClose, onRenamed }) {
  const [newName, setNewName] = useState(oldName);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) { onClose(); return; }
    setSaving(true);
    try {
      const r = await apiPut('/masters/departments/rename', { old_name: oldName, new_name: trimmed });
      toast.success(`Renamed "${oldName}" → "${trimmed}" · ${r.profiles_updated} profile(s), ${r.users_updated} user(s), ${r.employees_updated} employee(s) updated`);
      onRenamed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="rename-dept-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-bold text-sm text-[#3b4872] mb-2">Rename Department</div>
        <label className="text-[10px] uppercase text-slate-500 font-display block mb-1">New name for "{oldName}"</label>
        <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="rename-dept-input" />
        <p className="text-[10px] text-slate-500 mt-2">
          Cascades immediately to department profiles, users and employees — no separate save step.
        </p>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm" data-testid="rename-dept-cancel">Cancel</button>
          <button
            disabled={saving || !newName.trim() || newName.trim() === oldName}
            onClick={submit}
            className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm disabled:opacity-50"
            data-testid="rename-dept-confirm"
          >
            {saving ? 'Renaming…' : 'Confirm Rename'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── DeptReassignModal ────────────────────────────────────────────────────────
function DeptReassignModal({ removedDepts, affectedUsers, availableDepts, pendingEntries, onComplete, onCancel }) {
  const [pending, setPending] = useState(affectedUsers.map(u => ({ ...u, newDept: '' })));
  const [selected, setSelected] = useState(new Set());
  const [bulkDept, setBulkDept] = useState('');
  const [saving, setSaving] = useState(null); // user_id being saved or 'bulk'

  const allReassigned = pending.length === 0;

  const reassignOne = async (userId, newDept) => {
    if (!newDept) { toast.error('Select a new department first'); return; }
    setSaving(userId);
    try {
      await apiPut('/masters/departments/bulk-reassign', { user_ids: [userId], new_department: newDept });
      setPending(p => p.filter(u => u.user_id !== userId));
      setSelected(s => { const n = new Set(s); n.delete(userId); return n; });
      toast.success('User reassigned');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reassign failed'); }
    setSaving(null);
  };

  const reassignBulk = async () => {
    if (!bulkDept) { toast.error('Select a new department for bulk reassign'); return; }
    const ids = [...selected];
    setSaving('bulk');
    try {
      await apiPut('/masters/departments/bulk-reassign', { user_ids: ids, new_department: bulkDept });
      setPending(p => p.filter(u => !selected.has(u.user_id)));
      setSelected(new Set());
      setBulkDept('');
      toast.success(`${ids.length} user(s) reassigned to ${bulkDept}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk reassign failed'); }
    setSaving(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="dept-reassign-modal">
      <div className="bg-white rounded-sm border border-slate-200 w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="font-display font-bold text-sm text-rose-700 flex items-center gap-2">
              <Users size={14} /> Reassign users before deleting department{removedDepts.length > 1 ? 's' : ''}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {allReassigned
                ? 'All users reassigned. Ready to proceed.'
                : `${pending.length} user${pending.length !== 1 ? 's' : ''} must be moved to a new department before "${removedDepts.join('", "')}" can be deleted.`}
            </p>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        {/* User table */}
        <div className="flex-1 overflow-y-auto">
          {allReassigned ? (
            <div className="flex flex-col items-center justify-center py-12 text-emerald-600">
              <div className="text-2xl mb-2">✓</div>
              <p className="font-display font-bold">All users reassigned successfully</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox"
                      checked={selected.size === pending.length && pending.length > 0}
                      onChange={e => setSelected(e.target.checked ? new Set(pending.map(u => u.user_id)) : new Set())}
                      className="accent-[#3b4872]" data-testid="reassign-select-all" />
                  </th>
                  <th className="px-3 py-2 text-left font-display font-bold uppercase tracking-wider text-[10px]">Name</th>
                  <th className="px-3 py-2 text-left font-display font-bold uppercase tracking-wider text-[10px]">Email</th>
                  <th className="px-3 py-2 text-left font-display font-bold uppercase tracking-wider text-[10px]">Current Dept</th>
                  <th className="px-3 py-2 text-left font-display font-bold uppercase tracking-wider text-[10px]">New Department</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pending.map(u => (
                  <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`reassign-row-${u.user_id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(u.user_id)}
                        onChange={e => {
                          setSelected(s => {
                            const n = new Set(s);
                            e.target.checked ? n.add(u.user_id) : n.delete(u.user_id);
                            return n;
                          });
                        }} className="accent-[#3b4872]" data-testid={`reassign-check-${u.user_id}`} />
                    </td>
                    <td className="px-3 py-2 font-semibold">{u.name || '—'}</td>
                    <td className="px-3 py-2 text-slate-500">{u.email}</td>
                    <td className="px-3 py-2">
                      <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{u.department}</span>
                    </td>
                    <td className="px-3 py-2">
                      <select value={u.newDept} onChange={e => setPending(p => p.map(x => x.user_id === u.user_id ? { ...x, newDept: e.target.value } : x))}
                        className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1 focus:outline-none focus:border-[#3b4872]"
                        data-testid={`reassign-dept-${u.user_id}`}>
                        <option value="">— pick dept —</option>
                        {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => reassignOne(u.user_id, u.newDept)}
                        disabled={!u.newDept || saving === u.user_id}
                        className="px-2 py-1 text-[10px] bg-[#3b4872] text-white rounded-sm font-bold disabled:opacity-40 flex items-center gap-1"
                        data-testid={`reassign-btn-${u.user_id}`}>
                        {saving === u.user_id ? <Loader2 size={10} className="animate-spin" /> : <ChevronRight size={10} />}
                        Reassign
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Bulk bar */}
        {selected.size > 0 && !allReassigned && (
          <div className="px-5 py-2 bg-[#3b4872]/5 border-t border-[#3b4872]/20 flex items-center gap-3" data-testid="bulk-reassign-bar">
            <span className="text-xs font-bold text-[#3b4872]">{selected.size} selected</span>
            <select value={bulkDept} onChange={e => setBulkDept(e.target.value)}
              className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white focus:outline-none focus:border-[#3b4872] flex-1 max-w-xs"
              data-testid="bulk-reassign-dept">
              <option value="">— new department —</option>
              {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <button onClick={reassignBulk} disabled={!bulkDept || saving === 'bulk'}
              className="px-3 py-1.5 text-[11px] bg-[#3b4872] text-white rounded-sm font-bold disabled:opacity-40 flex items-center gap-1"
              data-testid="bulk-reassign-apply">
              {saving === 'bulk' ? <Loader2 size={10} className="animate-spin" /> : null}
              Apply to selected
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700">
            Cancel — restore removed departments
          </button>
          <button
            onClick={() => onComplete(pendingEntries)}
            disabled={!allReassigned}
            className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-xs font-display font-bold rounded-sm disabled:opacity-40"
            data-testid="reassign-confirm-delete">
            {allReassigned ? '✓ Confirm & Delete Department' : `${pending.length} user${pending.length !== 1 ? 's' : ''} remaining…`}
          </button>
        </div>
      </div>
    </div>
  );
}
